import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

/** A step's load state, when the caller knows it. */
export type StepperStepStatus = "complete" | "current" | "upcoming" | "unavailable";

export interface StepperStep {
  label: string;
  description?: string;
  optional?: boolean;
  /**
   * Explicit state for this step. Omit it and the state is derived from
   * `current`, which is what every wizard does today. Pass `"unavailable"` when
   * the step's status could not be loaded: the step is then never counted as
   * complete and never rendered as "todo" — it renders "Status unavailable",
   * because "we could not check" is a different fact from "not started yet".
   */
  status?: StepperStepStatus;
}

type StepState = StepperStepStatus | "error";

/**
 * Single resolution pass for a step's rendered state. Both the step list and
 * the "N of M complete" summary read their state from here, so the count can
 * never disagree with what is on screen.
 */
function resolveStepState(
  step: StepperStep,
  index: number,
  current: number,
  errorIndex?: number
): StepState {
  if (errorIndex === index) return "error";
  if (step.status) return step.status;
  if (index < current) return "complete";
  if (index === current) return "current";
  return "upcoming";
}

export interface StepperSummary {
  completeCount: number;
  totalCount: number;
  allComplete: boolean;
}

/** Derive the progress summary from the same states the steps render with. */
export function summarizeSteps(
  steps: StepperStep[],
  current: number,
  errorIndex?: number
): StepperSummary {
  const completeCount = steps.filter(
    (step, index) => resolveStepState(step, index, current, errorIndex) === "complete"
  ).length;
  return {
    completeCount,
    totalCount: steps.length,
    allComplete: steps.length > 0 && completeCount === steps.length,
  };
}

function StepperSummaryLine({ completeCount, totalCount }: StepperSummary) {
  if (totalCount === 0) return null;
  const allComplete = completeCount === totalCount;
  return (
    <div
      data-testid="stepper-summary"
      data-state={allComplete ? "complete" : "in-progress"}
      className="mb-2 text-xs"
    >
      {allComplete ? (
        <span className="font-semibold text-success">{`All ${totalCount} steps complete`}</span>
      ) : (
        <span className="text-muted">{`${completeCount} of ${totalCount} complete`}</span>
      )}
    </div>
  );
}

/** Figma Stepper 32236:65111 — bordered bar, 22px circles, flexible Gray/200 (line-soft) connectors. */
function CardStepper({
  steps,
  current,
  errorIndex,
  showSummary = false,
  className,
}: {
  steps: StepperStep[];
  current: number;
  errorIndex?: number;
  showSummary?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("overflow-x-auto rounded-[10px] border border-line-soft bg-surface px-4 py-3.5", className)}>
      {showSummary && <StepperSummaryLine {...summarizeSteps(steps, current, errorIndex)} />}
      <ol aria-label="Progress steps" className="flex min-w-max items-center">
        {steps.map((s, i) => {
          const state = resolveStepState(s, i, current, errorIndex);
          return (
            <li
              key={s.label}
              data-state={state}
              className={cn("flex items-center", i < steps.length - 1 && "flex-1")}
            >
              <span aria-current={state === "current" ? "step" : undefined} className="flex shrink-0 items-center gap-1.5">
                <span
                  aria-hidden
                  className={cn(
                    "grid size-[22px] shrink-0 place-items-center rounded-full text-[11px] font-semibold",
                    (state === "complete" || state === "current") && "bg-primary text-white",
                    state === "error" && "bg-danger text-white",
                    state === "unavailable" && "border border-line bg-surface text-subtle",
                    state === "upcoming" && "bg-disabled-soft text-muted"
                  )}
                >
                  {state === "complete" ? (
                    "✓"
                  ) : state === "error" ? (
                    <X className="size-3" />
                  ) : state === "unavailable" ? (
                    "—"
                  ) : (
                    i + 1
                  )}
                </span>
                <span
                  className={cn(
                    "whitespace-nowrap text-xs",
                    state === "current" ? "font-semibold text-ink" : "font-medium",
                    state === "complete" && "text-ink",
                    state === "error" && "font-semibold text-danger",
                    state === "unavailable" && "text-subtle",
                    state === "upcoming" && "text-muted"
                  )}
                >
                  {s.label}
                  {state === "complete" && <span className="sr-only"> (complete)</span>}
                  {state === "error" && <span className="sr-only"> (blocked)</span>}
                  {state === "unavailable" && <span className="sr-only"> (status unavailable)</span>}
                </span>
              </span>
              {i < steps.length - 1 && <span aria-hidden className="mx-2 h-px min-w-4 flex-1 bg-line-soft" />}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function Stepper({
  steps,
  current,
  errorIndex,
  compact = false,
  variant = "default",
  showSummary = false,
  className,
}: {
  steps: StepperStep[];
  current: number;
  errorIndex?: number;
  compact?: boolean;
  variant?: "default" | "card";
  /**
   * Render the derived "N of M complete" line above the steps. Off by default
   * so no existing wizard changes appearance; the count itself is always
   * derived from the rendered states (see `summarizeSteps`).
   */
  showSummary?: boolean;
  className?: string;
}) {
  if (variant === "card") {
    return (
      <CardStepper
        steps={steps}
        current={current}
        errorIndex={errorIndex}
        showSummary={showSummary}
        className={className}
      />
    );
  }
  const list = (
    <ol aria-label="Progress steps" className={cn("flex flex-wrap items-start", className)}>
      {steps.map((s, i) => {
        const state = resolveStepState(s, i, current, errorIndex);
        const isLast = i === steps.length - 1;
        return (
          <li
            key={s.label}
            data-state={state}
            aria-current={state === "current" ? "step" : undefined}
            className={cn("flex items-start", !compact && "min-w-[150px] flex-1")}
          >
            <div className="flex shrink-0 items-start gap-2 pr-2">
              <span
                aria-hidden
                className={cn(
                  "grid size-6 shrink-0 place-items-center rounded-full border text-[11px] font-bold",
                  state === "complete" && "border-success bg-success text-white",
                  state === "current" && "border-primary bg-primary text-white",
                  state === "error" && "border-danger bg-danger text-white",
                  state === "unavailable" && "border-line bg-surface text-subtle",
                  state === "upcoming" && "border-line bg-surface text-muted"
                )}
              >
                {state === "complete" ? (
                  <Check aria-hidden className="size-3.5" />
                ) : state === "error" ? (
                  <X aria-hidden className="size-3" />
                ) : state === "unavailable" ? (
                  "—"
                ) : (
                  i + 1
                )}
              </span>
              <div className="leading-tight">
                <div
                  className={cn(
                    "text-xs font-semibold",
                    state === "upcoming" ? "text-muted" : "text-ink",
                    state === "error" && "text-danger",
                    state === "unavailable" && "text-subtle"
                  )}
                >
                  {s.label}
                  {state === "complete" && <span className="sr-only"> (complete)</span>}
                  {state === "error" && <span className="sr-only"> (blocked)</span>}
                  {state === "unavailable" && <span className="sr-only"> (status unavailable)</span>}
                </div>
                {!compact && state === "unavailable" && (
                  <div className="text-[11px] text-muted">Status unavailable</div>
                )}
                {!compact && s.description && <div className="text-[11px] text-muted">{s.description}</div>}
                {s.optional && <div className="text-[11px] text-subtle">Optional</div>}
              </div>
            </div>
            {!isLast && !compact && (
              <div aria-hidden className={cn("mt-3 h-px min-w-6 flex-1 bg-line")} />
            )}
          </li>
        );
      })}
    </ol>
  );
  if (!showSummary) return list;
  // The summary sits outside the `<ol>`: a list element may only contain `li`.
  return (
    <div>
      <StepperSummaryLine {...summarizeSteps(steps, current, errorIndex)} />
      {list}
    </div>
  );
}
